import { defineMessages } from 'react-intl';

import type { Props as MoreAboutProps } from '#src/components/seo/MoreAbout/index.tsx';
import { MoreAbout } from '#src/components/seo/MoreAbout/index.tsx';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useIntlCountries } from '#src/hooks/useIntlCountries.ts';

export const messages = defineMessages({
  howOld: {
    id: 'Seo.MoreAbout.howOld',
    defaultMessage: 'How old is {displayName}?',
  },
  howOldAnswer: {
    id: 'Seo.MoreAbout.howOldAnswerV2',
    defaultMessage: '{displayName} is {age} years old.',
  },
  howOldAnswerWithBirthDate: {
    id: 'Seo.MoreAbout.howOldAnswerWithBirthDate',
    defaultMessage:
      '{displayName} is {age} years old and was born on {birthDate}.',
  },
  howTall: {
    id: 'Seo.MoreAbout.howTall',
    defaultMessage: 'How tall is {displayName}?',
  },
  howTallAnswer: {
    id: 'Seo.MoreAbout.howTallAnswer',
    defaultMessage: '{displayName} measures {height}m.',
  },
  whatTeam: {
    id: 'Seo.MoreAbout.whatTeam',
    defaultMessage: 'What team does {displayName} play for?',
  },
  whatTeamAnswerClub: {
    id: 'Seo.MoreAbout.whatTeamAnswerClub',
    defaultMessage: '{displayName} is playing for {club} in club.',
  },
  whatTeamAnswerNational: {
    id: 'Seo.MoreAbout.whatTeamAnswerNational',
    defaultMessage: 'He is also playing for {nationalTeam} in national team.',
  },
  whatNumber: {
    id: 'Seo.MoreAbout.whatNumber',
    defaultMessage: 'What number is {displayName}?',
  },
  whatNumberAnswer: {
    id: 'Seo.MoreAbout.whatNumberAnswer',
    defaultMessage:
      'The current jersey number of {displayName} with {club} is {shirtNumber}.',
  },
  whatCountry: {
    id: 'Seo.MoreAbout.whereFrom',
    defaultMessage: 'Where is {displayName} from?',
  },
  whatCountryAnswer: {
    id: 'Seo.MoreAbout.whatCountryAnswer',
    defaultMessage: '{displayName} is from {country}.',
  },
  howManyGoals: {
    id: 'Seo.MoreAbout.howManyGoals',
    defaultMessage: 'How many goals has {displayName} scored?',
  },
  howManyGoalsAnswer: {
    id: 'Seo.MoreAbout.howManyGoalsAnswerV2',
    defaultMessage:
      '{displayName} has scored {goals, plural, one {# goal} other {# goals}} with {club}.',
  },
});

type Props = {
  displayName: string;
  club?: string;
  nationalTeamCode?: string;
  countryCode?: string;
  height?: number | null;
  age: number;
  birthDate?: Date | null;
  shirtNumber: number | null;
  specificItems?: MoreAboutProps['items'];
  withContainer?: boolean;
};

export const MoreAboutPlayer = ({
  displayName,
  club,
  nationalTeamCode,
  age,
  birthDate,
  shirtNumber,
  countryCode,
  height,
  specificItems,
  withContainer = true,
}: Props) => {
  const { formatMessage, formatDate, formatNumber } = useIntlContext();
  const countries = useIntlCountries();

  const country = countries.find(n => n.value === countryCode)?.label;
  const nationalTeam = countries.find(n => n.value === nationalTeamCode)?.label;

  return (
    <MoreAbout
      displayName={displayName}
      withContainer={withContainer}
      items={[
        {
          question: formatMessage(messages.howOld, { displayName }),
          answer: formatMessage(
            birthDate
              ? messages.howOldAnswerWithBirthDate
              : messages.howOldAnswer,
            {
              birthDate:
                birthDate &&
                formatDate(birthDate, {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  timeZone: 'UTC',
                }),
              displayName,
              age,
            }
          ),
        },
        height && {
          question: formatMessage(messages.howTall, { displayName }),
          answer: formatMessage(messages.howTallAnswer, {
            displayName,
            height: formatNumber(height / 100, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          }),
        },
        club && {
          question: formatMessage(messages.whatTeam, { displayName }),
          answer: `${formatMessage(messages.whatTeamAnswerClub, {
            displayName,
            club,
          })} ${
            nationalTeam
              ? formatMessage(messages.whatTeamAnswerNational, { nationalTeam })
              : ''
          }`,
        },
        club &&
          shirtNumber && {
            question: formatMessage(messages.whatNumber, { displayName }),
            answer: formatMessage(messages.whatNumberAnswer, {
              displayName,
              club,
              shirtNumber,
            }),
          },
        country && {
          question: formatMessage(messages.whatCountry, { displayName }),
          answer: formatMessage(messages.whatCountryAnswer, {
            displayName,
            country,
          }),
        },
        ...(specificItems || []),
      ].filter(Boolean)}
    />
  );
};
